// ─── Custom theme runtime ────────────────────────────────────────────
// Applies a user's custom theme as INLINE CSS custom properties on <html>.
// Inline props win the cascade unconditionally and are read by getComputedStyle
// (so Chart.js theme reads keep working). setProperty is also the security
// boundary: the CSSOM rejects any value that isn't valid CSS grammar, and we
// only ever pass allowlisted names + validated values (see @pcp/shared).

import { CUSTOM_TOKEN_NAMES, isValidThemeTokenValue, type CustomTheme } from '@pcp/shared';

const PROP_NAMES: string[] = [...CUSTOM_TOKEN_NAMES].map((n) => `--${n}`);

/** Remove every inline custom-theme property → full revert to the stylesheet. */
export function clearCustomTokens(root: HTMLElement = document.documentElement): void {
  for (const p of PROP_NAMES) root.style.removeProperty(p);
}

/**
 * Apply a custom theme's tokens for the resolved mode as inline vars. Always
 * clears first (so switching themes/modes never leaves stale props). No-op when
 * contrast is 'high' (the AAA high-contrast CSS must win) or the theme is null.
 */
export function applyCustomTokens(
  theme: CustomTheme | null | undefined,
  resolvedMode: 'light' | 'dark',
  contrast: 'normal' | 'high',
  root: HTMLElement = document.documentElement,
): void {
  clearCustomTokens(root);
  if (!theme || contrast === 'high') return;
  const tokens = resolvedMode === 'dark' ? theme.dark : theme.light;
  for (const [name, value] of Object.entries(tokens)) {
    if (isValidThemeTokenValue(name, value)) root.style.setProperty(`--${name}`, value);
  }
}

/**
 * Read the COMPUTED value of every editable token under a given base palette +
 * mode, by briefly swapping <html> attributes. Synchronous: the browser does
 * not paint between the swap and the restore, so there's no flicker. Used to
 * seed the editor with real starting colours from a chosen base theme.
 */
export function readBaseTokens(base: string, mode: 'light' | 'dark'): Record<string, string> {
  const root = document.documentElement;
  const prev = {
    theme: root.getAttribute('data-theme'),
    mode: root.getAttribute('data-mode'),
    contrast: root.getAttribute('data-contrast'),
  };
  // Inline custom props would shadow the base palette — strip them for the read.
  const savedInline: Record<string, string> = {};
  for (const p of PROP_NAMES) {
    const v = root.style.getPropertyValue(p);
    if (v) { savedInline[p] = v; root.style.removeProperty(p); }
  }
  const out: Record<string, string> = {};
  try {
    root.setAttribute('data-theme', base);
    root.setAttribute('data-mode', mode);
    root.removeAttribute('data-contrast');
    const cs = getComputedStyle(root);
    for (const name of CUSTOM_TOKEN_NAMES) out[name] = cs.getPropertyValue(`--${name}`).trim();
  } finally {
    // Restore exactly, even if the read throws (never leave the DOM swapped).
    prev.theme != null ? root.setAttribute('data-theme', prev.theme) : root.removeAttribute('data-theme');
    prev.mode != null ? root.setAttribute('data-mode', prev.mode) : root.removeAttribute('data-mode');
    prev.contrast != null ? root.setAttribute('data-contrast', prev.contrast) : root.removeAttribute('data-contrast');
    for (const [p, v] of Object.entries(savedInline)) root.style.setProperty(p, v);
  }
  return out;
}

// ─── Live cross-window preview (BroadcastChannel) ─────────────────────
// The editor window posts the in-progress theme; every app window applies it
// live WITHOUT persisting, and reverts on 'stop' / channel close.

export const THEME_PREVIEW_CHANNEL = 'pcp-theme-preview';

export type ThemePreviewMessage =
  | { type: 'preview'; theme: CustomTheme; mode: 'light' | 'dark'; contrast: 'normal' | 'high' }
  // Editor liveness — lets a receiver auto-revert if the editor window is
  // hard-closed (BroadcastChannel sends no close event).
  | { type: 'heartbeat' }
  // Editor saved — receivers sync their own store + apply (cross-window stores
  // don't auto-share, so a plain 'stop' would revert to a stale selection).
  | { type: 'saved'; theme: CustomTheme; activeCustomId: string }
  | { type: 'stop' };

export function openThemePreviewChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(THEME_PREVIEW_CHANNEL);
}

// While a live cross-window preview is active, the normal store-driven apply must
// stand down so the preview wins. Module-scoped per window.
let _previewing = false;
export function isThemePreviewing(): boolean { return _previewing; }
export function setThemePreviewing(v: boolean): void { _previewing = v; }

// ─── WCAG contrast (editor readouts) ──────────────────────────────────

/** Parse #hex / rgb()/rgba() to [r,g,b] 0-255 (alpha ignored). null if unknown. */
function parseRgb(value: string): [number, number, number] | null {
  const s = value.trim();
  const hex = s.match(/^#([0-9a-fA-F]{3,8})$/);
  if (hex) {
    let h = hex[1];
    if (h.length === 3 || h.length === 4) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h.slice(0, 6), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const fn = s.match(/^rgba?\(\s*([0-9.]+)[ ,]+([0-9.]+)[ ,]+([0-9.]+)/i);
  if (fn) return [Number(fn[1]), Number(fn[2]), Number(fn[3])];
  return null;
}

function relLuminance([r, g, b]: [number, number, number]): number {
  const lin = [r, g, b].map((c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/** WCAG contrast ratio (1–21) between two colors, or null if unparseable. */
export function contrastRatio(fg: string, bg: string): number | null {
  const a = parseRgb(fg);
  const b = parseRgb(bg);
  if (!a || !b) return null;
  const l1 = relLuminance(a);
  const l2 = relLuminance(b);
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

/** WCAG rating for normal-size text. */
export function contrastRating(ratio: number): 'AAA' | 'AA' | 'fail' {
  if (ratio >= 7) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  return 'fail';
}
